import { useEffect, useMemo, useState } from "react";
import api from "../../service/api";

export type InstitutionUser = {
  id: string;
  name: string;
  user_type?: string;
  email?: string;
};

type Props = {
  allowAll: boolean;
  selectedIds: string[];
  onAllowAllChange: (value: boolean) => void;
  onSelectedIdsChange: (ids: string[]) => void;
};

/**
 * Seleção de instituições autorizadas a cadastrar participantes no evento.
 */
export function EventInstitutionsPicker({
  allowAll,
  selectedIds,
  onAllowAllChange,
  onSelectedIdsChange,
}: Props) {
  const [institutions, setInstitutions] = useState<InstitutionUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/users");
        const list = (Array.isArray(data) ? data : [])
          .filter((user: InstitutionUser) =>
            ["USER", "USERENTERPRISE"].includes(String(user.user_type ?? ""))
          )
          .sort((a: InstitutionUser, b: InstitutionUser) =>
            a.name.localeCompare(b.name, "pt-BR")
          );
        if (active) setInstitutions(list);
      } catch {
        if (active) setInstitutions([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return institutions;
    return institutions.filter(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        String(item.email ?? "")
          .toLowerCase()
          .includes(q)
    );
  }, [filter, institutions]);

  const allFilteredSelected =
    filtered.length > 0 &&
    filtered.every((item) => selectedIds.includes(item.id));

  function toggleOne(id: string) {
    if (selectedIds.includes(id)) {
      onSelectedIdsChange(selectedIds.filter((item) => item !== id));
      return;
    }
    onSelectedIdsChange([...selectedIds, id]);
  }

  function toggleFiltered() {
    if (allFilteredSelected) {
      const remove = new Set(filtered.map((item) => item.id));
      onSelectedIdsChange(selectedIds.filter((id) => !remove.has(id)));
      return;
    }
    const next = new Set(selectedIds);
    filtered.forEach((item) => next.add(item.id));
    onSelectedIdsChange(Array.from(next));
  }

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">
          Instituições no totem
        </p>
        <p className="text-xs text-zinc-500 dark:text-zinc-400">
          Escolha quem poderá cadastrar participantes neste evento. A lista
          aparece no app do totem.
        </p>
      </div>

      <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-700 dark:text-zinc-200">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-zinc-300"
          checked={allowAll}
          onChange={(e) => {
            const checked = e.target.checked;
            onAllowAllChange(checked);
            if (checked) {
              onSelectedIdsChange([]);
            }
          }}
        />
        Todas as instituições
      </label>

      {!allowAll ? (
        <>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Buscar instituição..."
              className="min-h-10 flex-1 rounded-lg border border-zinc-200 bg-white px-3 text-sm text-zinc-800 outline-none focus:border-emerald-500 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100"
            />
            <button
              type="button"
              onClick={toggleFiltered}
              className="rounded-lg border border-zinc-200 px-3 py-2 text-xs font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
            >
              {allFilteredSelected
                ? "Limpar filtradas"
                : "Selecionar filtradas"}
            </button>
          </div>

          <div className="max-h-56 overflow-y-auto rounded-lg border border-zinc-100 dark:border-zinc-800">
            {loading ? (
              <p className="p-3 text-sm text-zinc-500">Carregando...</p>
            ) : filtered.length === 0 ? (
              <p className="p-3 text-sm text-zinc-500">
                Nenhuma instituição encontrada.
              </p>
            ) : (
              filtered.map((item) => {
                const checked = selectedIds.includes(item.id);
                return (
                  <label
                    key={item.id}
                    className="flex cursor-pointer items-center gap-3 border-b border-zinc-100 px-3 py-2 last:border-b-0 hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-800/60"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-zinc-300"
                      checked={checked}
                      onChange={() => toggleOne(item.id)}
                    />
                    <span className="text-sm text-zinc-800 dark:text-zinc-100">
                      {item.name}
                    </span>
                  </label>
                );
              })
            )}
          </div>

          <p className="text-xs text-zinc-500">
            {selectedIds.length} selecionada(s)
          </p>
        </>
      ) : null}
    </div>
  );
}
